/* eslint-disable max-len */
import type { RouteLocationNormalized, RouteRecordRaw, Router } from 'vue-router'

import { register as registerAddin } from '~/contexts/addin/register'
import { register as registerAdminTenant } from '~/contexts/admin-tenant/register'
import { register as registerAura } from '~/contexts/aura/register'
import { register as registerCalls } from '~/contexts/calls/register'
import { register as registerCommunity } from '~/contexts/community/register'
import { register as registerDm } from '~/contexts/dm/register'
import { register as registerHome } from '~/contexts/home/register'
import { register as registerLogout } from '~/contexts/logout/register'
import { register as registerMe } from '~/contexts/me/register'
import { register as registerNetwork } from '~/contexts/network/register'
import { register as registerNotification } from '~/contexts/notification/register'
import { register as registerRitual } from '~/contexts/ritual/register'
import LeftNavigationLayout from '~/layouts/left-navigation-layout.vue'

import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import type { IRegisterOpt } from '~/features-shared/libs/interfaces/register'
import { logger } from '~/features-shared/libs/utils/logger'

import type { NavigationGuard } from '~/typings/vue-router'

/**
 * Register a list of routes for the given namespace
 * @param router The router instance
 * @param namespace The context namespace (eg: "community")
 * @param context The context attached to the namespace
 * @param routes A list of routes to register on the namespace
 */
function addRouteWithNamespace(router: Router, namespace: string, context: EContext, routes: RouteRecordRaw[]) {
  logger.debug(`Register routes with namespace "${namespace}"`, routes)
  router.addRoute({
    component: LeftNavigationLayout,
    path: `/${namespace}`,
    children: routes,
    meta: {
      context,
    },
  })
}

/**
 * Check the given route is from the given namespace
 * @param route The current route
 * @param namespace The context namespace (eg: "community")
 */
function isCurrentRouteFromNamespace(route: RouteLocationNormalized, namespace: string) {
  return route.path.startsWith(`/${namespace}`)
}

/**
 * Register a "navigation guard" for the given context
 * @param router The router instance
 * @param namespace The context namespace (eg: "community")
 * @param guard The callback to execute when navigation to the context is executed
 */
function addNavigationGuardWithNamespace(router: Router, namespace: string, isMounted: Ref<boolean>, guard: NavigationGuard) {
  logger.debug(`Register navigation guard with namespace "${namespace}"`)

  router.beforeResolve((to, from, next) => {
    if (isCurrentRouteFromNamespace(to, namespace)) {
      logger.debug(`Execute navigation guard with namespace "${namespace}"`)
      return guard(to, from, next, isMounted, router.push)
    }

    next()
  })
}

/**
 * Register a "navigation error" callback for the given context
 * @param router The router instance
 * @param namespace The context namespace (eg: "community")
 * @param isMounted A ref to know if the app has been mounted or not
 * @param handler The callback to execute when a router error is thrown
 */
function addNavigationErrorHandlerWithNamespace(
  router: Router,
  namespace: string,
  isMounted: Ref<boolean>,
  handler: Parameters<Router['onError']>[0],
) {
  logger.debug(`Register navigation error handler with namespace "${namespace}"`)

  router.onError((error, to, from) => {
    if (isCurrentRouteFromNamespace(to, namespace)) {
      logger.log(`Execute navigation error handler with namespace "${namespace}"`)
      return handler(error, to, from)
    }
  })
}

function registerContext(registerCallback: (options: IRegisterOpt) => void, { router, namespace, context, isMounted }: {
  router: Router
  namespace: string
  context: EContext
  isMounted: Ref<boolean>
}) {
  logger.debug(`Register context "${namespace}"`)

  registerCallback({
    addRoute: addRouteWithNamespace.bind(null, router, namespace, context),
    addNavigationGuard: addNavigationGuardWithNamespace.bind(null, router, namespace, isMounted),
    addNavigationErrorHandler: addNavigationErrorHandlerWithNamespace.bind(null, router, namespace, isMounted),
  })
}

const NAMESPACE_ADDIN = 'addin'
const NAMESPACE_AURA = 'aura'
const NAMESPACE_ADMIN_TENANT = 'admin-tenant'
const NAMESPACE_CALLS = 'calls'
const NAMESPACE_COMMUNITY = 'community'
const NAMESPACE_DM = 'dm'
const NAMESPACE_HOME = ''
const NAMESPACE_LOGOUT = 'logout'
const NAMESPACE_ME = 'me'
const NAMESPACE_NETWORK = 'network'
const NAMESPACE_NOTIFICATIONS = 'notifications'
const NAMESPACE_RITUAL = 'rituals'

/**
 * Register context to Swym App
 * @param router the router instance
 * @param isMounted  A ref to know if the app has been mounted or not
 */
export function register(router: Router, isMounted: Ref<boolean>) {
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerAddin, { router, namespace: NAMESPACE_ADDIN, context: EContext.NULL, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerAdminTenant, { router, namespace: NAMESPACE_ADMIN_TENANT, context: EContext.ADMIN, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerAura, { router, namespace: NAMESPACE_AURA, context: EContext.AURA, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerCalls, { router, namespace: NAMESPACE_CALLS, context: EContext.CALLS, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerCommunity, { router, namespace: NAMESPACE_COMMUNITY, context: EContext.COMMUNITY, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerDm, { router, namespace: NAMESPACE_DM, context: EContext.DM, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerHome, { router, namespace: NAMESPACE_HOME, context: EContext.COMMUNITY, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerLogout, { router, namespace: NAMESPACE_LOGOUT, context: EContext.NULL, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerMe, { router, namespace: NAMESPACE_ME, context: EContext.ME, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerNetwork, { router, namespace: NAMESPACE_NETWORK, context: EContext.NETWORK, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerNotification, { router, namespace: NAMESPACE_NOTIFICATIONS, context: EContext.NOTIFICATIONS, isMounted })
  // @ts-expect-error I dont want to import all routes here so we'll just remove silently the TS error
  registerContext(registerRitual, { router, namespace: NAMESPACE_RITUAL, context: EContext.RITUALS, isMounted })
}
